import {
    Box,
    Button,
    Dialog,
    DialogProps,
    Grid,
    TextField,
} from "@material-ui/core";
import {
    Collections,
    Infraction,
    Protest,
    PUBLICS_CHAMPIONSHIPS_STORE_AS,
    RaceControl,
    Round,
    useChampionshipDrivers,
    useCollection,
    usePublicsChampionships,
    useSeasonOptions,
    useThemeBreakpoints,
} from "api";
import { Form, Formik } from "formik";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import React, { useContext, useMemo } from "react";
import { useHistory } from "react-router";
import {
    InputFieldFormik,
    LoggedUserContext,
    Message,
    SubmitButton,
    Title,
    yupMaxLengthField,
    yupMinLengthField,
    yupRequiredField,
} from "ui/componentes";
import { sendDefense } from "ui/views";
import { LIMIT_OF_DAYS_TO_DEFENSE } from "ui/views/Protests/hooks/useIsLimitToProtestDefenseExcedeed";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { RootState } from "redux/model";

interface ProtestAnswerFormProps {
    protestId: string;
}

export const DefenseValidation = Yup.object().shape({
    defense: Yup.string()
        .required(yupRequiredField)
        .min(50, yupMinLengthField)
        .max(300, yupMaxLengthField),
});

const ProtestAnswerForm: React.FC<ProtestAnswerFormProps & DialogProps> = ({
    protestId,
    ...dialogProps
}) => {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const loggedUser = useContext(LoggedUserContext);

    const protest =
        useCollection<Protest>({
            collection: Collections.PROTESTS,
            doc: protestId || "-1",
        }) || {};

    const {
        authorDriverId,
        championshipId,
        infractionId,
        raceControlId,
        roundId,
        seasonId,
    } = useMemo(() => {
        const {
            championshipId = "-1",
            roundId = "-1",
            raceControlId = "-1",
            infractionId = "-1",
            authorDriverId = "-1",
            seasonId = "-1",
        } = protest;
        return {
            championshipId,
            roundId,
            raceControlId,
            infractionId,
            authorDriverId,
            seasonId,
        };
    }, [protest]);

    usePublicsChampionships();
    const championships =
        useSelector(
            (state: RootState) =>
                state.firestore.data[PUBLICS_CHAMPIONSHIPS_STORE_AS]
        ) || {};

    const round =
        useCollection<Round>({
            collection: Collections.ROUNDS,
            doc: roundId,
        }) || {};

    const raceControl =
        useCollection<RaceControl>({
            collection: Collections.RACES_CONTROL,
            doc: raceControlId,
        }) || {};

    const infraction =
        useCollection<Infraction>({
            collection: Collections.INFRACTIONS,
            doc: infractionId,
        }) || {};

    const [
        dateLimitToAnswerExceeded,
        dateLimitToAnswer,
        protestDate,
        defenseDate,
    ] = useMemo(() => {
        if (protest.createdAt) {
            let protestDate = DateTime.fromFormat(
                protest.createdAt,
                "yyyy-MM-dd HH:mm:ss"
            );
            const date = protestDate.toFormat("yyyy-MM-dd");
            const dateLimitToAnswer = protestDate.plus({
                day: LIMIT_OF_DAYS_TO_DEFENSE,
            });
            return [
                dateLimitToAnswer.toFormat("yyyy-MM-dd") > date,
                dateLimitToAnswer.toFormat("dd/MM/yyyy"),
                protestDate.toFormat("dd/MM/yyyy"),
                DateTime.fromFormat(
                    protest.defenseAt,
                    "yyyy-MM-dd HH:mm:ss"
                ).toFormat("dd/MM/yyyy HH:mm:ss"),
            ];
        }
        return [false, "", "", ""];
    }, [protest]);

    const drivers = useChampionshipDrivers(championshipId);

    const protestAuthor = useMemo(() => {
        if (drivers && authorDriverId) {
            const author = drivers.filter(
                (driver) => driver.id === authorDriverId
            );
            if (author && author.length > 0) {
                return `#${author[0].usedNumber} - ${author[0].firstName} ${author[0].lastName}`;
            }
        }
        return "";
    }, [authorDriverId, drivers]);

    const canNotIncludeDefense =
        dateLimitToAnswerExceeded ||
        !!protest.defense ||
        !protest.id ||
        protest.protestedUserId !== loggedUser.id;

    const seasonById = useSeasonOptions().reduce(
        (previousValue, currentValue) => ({
            ...previousValue,
            [currentValue.value]: currentValue.label,
        }),
        {}
    );

    const { isDownXs } = useThemeBreakpoints();

    return (
        <Dialog
            open={dialogProps.open}
            onClose={(event, reason) => {
                if (dialogProps.onClose) {
                    dialogProps.onClose(event, reason);
                }
            }}
            maxWidth="md"
            fullScreen={isDownXs}
        >
            <Formik
                enableReinitialize={true}
                initialValues={{
                    defense: protest.defense || "",
                    protestId: protest.id,
                }}
                validationSchema={DefenseValidation}
                onSubmit={(values, { setSubmitting }) => {
                    sendDefense(values, setSubmitting, enqueueSnackbar).then(
                        () => {
                            if (dialogProps.onClose) {
                                dialogProps.onClose({}, "escapeKeyDown");
                            }
                        }
                    );
                }}
            >
                {({ values, isSubmitting }) => {
                    return (
                        <Form>
                            <Box padding={3}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <Title text="Envio de defesa" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Message
                                            show={
                                                dateLimitToAnswerExceeded &&
                                                !protest.defense
                                            }
                                            variant="error"
                                            message={`Prazo para defesa encerrado. A data limite era até ${dateLimitToAnswer} às 23:59:59hrs.`}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Message
                                            show={
                                                !!protest.defense &&
                                                protest.authorUserId !==
                                                    loggedUser.id
                                            }
                                            variant="info"
                                            message={`Já foi enviado uma defesa para esse protesto no dia ${defenseDate}`}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            disabled
                                            label="Temparada"
                                            value={seasonById[seasonId] || ""}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            disabled
                                            label="Piloto protestante"
                                            value={protestAuthor}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            disabled
                                            label="Campeonato"
                                            value={
                                                championships[championshipId]
                                                    ? championships[
                                                          championshipId
                                                      ].name
                                                    : ""
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            disabled
                                            label="Etapa"
                                            value={round.trackName || ""}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            disabled
                                            value={`Bateria ${
                                                raceControl.raceNumber || ""
                                            }`}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            disabled
                                            label="Volta"
                                            value={protest.lap || ""}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            disabled
                                            label="Data do protesto"
                                            value={protestDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            disabled
                                            label="Descrição do incidente"
                                            value={protest.complaint || ""}
                                            multiline
                                            rows={6}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            disabled
                                            label="Baseado na infração"
                                            value={
                                                infraction.description
                                                    ? `${infraction.key} - ${infraction.description}`
                                                    : ""
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputFieldFormik
                                            required
                                            autoFocus
                                            disabled={canNotIncludeDefense}
                                            id="defense"
                                            name="defense"
                                            label="Defesa"
                                            multiline
                                            rows={6}
                                            helperText={`Max.: ${values.defense.length} / 300`}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={canNotIncludeDefense ? 12 : 6}
                                    >
                                        <Button
                                            fullWidth
                                            disabled={isSubmitting}
                                            onClick={() => {
                                                history.push("/protest");
                                                if (dialogProps.onClose) {
                                                    dialogProps.onClose(
                                                        {},
                                                        "escapeKeyDown"
                                                    );
                                                }
                                            }}
                                        >
                                            Fechar
                                        </Button>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={6}
                                        hidden={canNotIncludeDefense}
                                    >
                                        <SubmitButton fullWidth>
                                            {isSubmitting
                                                ? "Enviando defesa"
                                                : "Enviar defesa"}
                                        </SubmitButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

export { ProtestAnswerForm };
